import { db } from '@/config/firebase';
import { 
  collection, 
  query, 
  where, 
  orderBy, 
  limit, 
  getDocs, 
  addDoc, 
  updateDoc,
  doc,
  onSnapshot,
  Timestamp,
  serverTimestamp
} from 'firebase/firestore';
import { ChatMessage, ChatThread } from '@/types/chatTypes';

export const chatService = {
  // Get chat threads for a user (student or instructor)
  async getChatThreads(userId: string, role: 'student' | 'instructor'): Promise<ChatThread[]> {
    const threadsRef = collection(db, 'chatThreads');
    const q = query(
      threadsRef,
      where(`participants.${role}.id`, '==', userId),
      orderBy('lastMessageTime', 'desc')
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as ChatThread[];
  },

  // Get messages for a specific thread
  async getMessages(threadId: string): Promise<ChatMessage[]> {
    const messagesRef = collection(db, 'chatThreads', threadId, 'messages');
    const q = query(messagesRef, orderBy('timestamp', 'asc'));

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as ChatMessage[];
  },

  // Send a new message
  async sendMessage(
    threadId: string,
    message: Omit<ChatMessage, 'id' | 'timestamp' | 'read'>
  ): Promise<void> {
    const messagesRef = collection(db, 'chatThreads', threadId, 'messages');
    const threadRef = doc(db, 'chatThreads', threadId);

    // Add the message
    await addDoc(messagesRef, {
      ...message,
      timestamp: serverTimestamp(),
      read: false
    });

    // Update thread with last message info
    await updateDoc(threadRef, {
      lastMessage: message.content,
      lastMessageTime: serverTimestamp(),
      unreadCount: message.receiverRole === 'student' ? 1 : 0
    });
  },

  // Mark messages as read
  async markMessagesAsRead(threadId: string, userId: string): Promise<void> {
    const messagesRef = collection(db, 'chatThreads', threadId, 'messages');
    const q = query(
      messagesRef,
      where('receiverId', '==', userId),
      where('read', '==', false)
    );

    const snapshot = await getDocs(q);
    const batch = db.batch();

    snapshot.docs.forEach(doc => {
      batch.update(doc.ref, { read: true });
    });

    // Update thread unread count
    const threadRef = doc(db, 'chatThreads', threadId);
    batch.update(threadRef, { unreadCount: 0 });

    await batch.commit();
  },

  // Create a new chat thread when an instructor is assigned
  async createChatThread(
    enrollmentId: string,
    studentId: string,
    studentName: string,
    instructorId: string,
    instructorName: string
  ): Promise<string> {
    const threadsRef = collection(db, 'chatThreads');
    const threadData = {
      enrollmentId,
      participants: {
        student: {
          id: studentId,
          name: studentName
        },
        instructor: {
          id: instructorId,
          name: instructorName
        }
      },
      lastMessageTime: serverTimestamp(),
      unreadCount: 0
    };

    const docRef = await addDoc(threadsRef, threadData);
    return docRef.id;
  },

  // Subscribe to new messages in a thread
  subscribeToMessages(
    threadId: string,
    callback: (messages: ChatMessage[]) => void
  ): () => void {
    const messagesRef = collection(db, 'chatThreads', threadId, 'messages');
    const q = query(messagesRef, orderBy('timestamp', 'asc'));

    return onSnapshot(q, (snapshot) => {
      const messages = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as ChatMessage[];
      callback(messages);
    });
  }
}; 